import { useEffect, useState } from "react";
import { collection, doc, limit, onSnapshot, orderBy, query } from "firebase/firestore";
import { DEFAULT_REMINDER_BODY, REMINDER_FIELDS, TextingSettingsSchema, fillReminder, paths, type Feedback, type MessageLog, type TextingSettings } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { useJobSettings } from "../../hooks/useJobSettings";
import { Link } from "wouter";
import { useEntitlements } from "../../hooks/useEntitlements";
import { PlanLocked } from "../../components/PlanLocked";

// Texting: what goes to customers automatically (all off until turned on),
// when it may go out, where reviews go, and what's been sent, skipped and
// said back. The "on my way" and "who's coming" buttons on a job work
// either way.

const TIMEZONES = ["America/Los_Angeles", "America/Phoenix", "America/Denver", "America/Chicago", "America/New_York", "Pacific/Honolulu", "America/Anchorage"];
const TEXTAREA = "w-full rounded-lg border border-steel/30 bg-white px-2 py-1 text-xs text-ink focus:border-brandorange focus:outline-none";
const SAMPLE = { firstName: "Jordan", company: "Your company", when: "tomorrow (Tue, Oct 27), 8 AM - 10 AM", date: "tomorrow (Tue, Oct 27)", time: "8 AM - 10 AM", tech: "Ayden", address: "123 Main St" };

export function TextingPage() {
  const { tenantId } = useAuth();
  const { types } = useJobSettings(tenantId);
  const [form, setForm] = useState<TextingSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [messages, setMessages] = useState<Array<MessageLog & { id: string }>>([]);
  const [feedback, setFeedback] = useState<Array<Feedback & { id: string }>>([]);
  const autosOn = useEntitlements().hasFeature("autoTexts");

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(doc(db, paths.tenantTextingSettings(tenantId)), (snap) => {
        const s = TextingSettingsSchema.parse(snap.data() ?? {});
        setForm((f) => f ?? { ...s, timezone: s.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone });
      }),
      onSnapshot(query(collection(db, paths.tenantMessages(tenantId)), orderBy("createdAt", "desc"), limit(30)), (snap) =>
        setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as MessageLog) }))),
      ),
      onSnapshot(query(collection(db, paths.tenantFeedback(tenantId)), orderBy("createdAt", "desc"), limit(30)), (snap) =>
        setFeedback(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Feedback) }))),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  async function save() {
    if (!tenantId || !form) return;
    setBusy(true);
    setNote("");
    try {
      const { updatedAt: _u, ...settings } = form;
      await call("setTextingSettings", { tenantId, settings });
      setNote("Saved.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!form) return <p className="text-sm text-steel">Loading…</p>;
  const set = (patch: Partial<TextingSettings>) => setForm({ ...form, ...patch });
  const setReview = (patch: Partial<TextingSettings["reviewRequest"]>) => set({ reviewRequest: { ...form.reviewRequest, ...patch } });
  const setReminder = (patch: Partial<TextingSettings["reminder"]>) => set({ reminder: { ...form.reminder, ...patch } });
  const setReminderType = (t: string, v: { enabled: boolean; body: string }) => setReminder({ jobTypes: { ...form.reminder.jobTypes, [t]: v } });
  const preview = (body: string) => fillReminder(body || form.reminder.body || DEFAULT_REMINDER_BODY, { ...SAMPLE, phone: form.companyPhone || "702-555-0100" });

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="text-lg font-bold">Texting</h1>
      <p className="text-xs text-steel">
        Texts to your customers. "On my way" and "who's coming" can always be sent from a job. The automatic ones below are off
        until you turn them on. Customers who reply STOP, or who you've marked "no texts", are never texted.
      </p>

      <Card className="space-y-3">
        <CardTitle>Your company</CardTitle>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Phone customers should call</Label>
            <Input value={form.companyPhone} placeholder="702-555-0100" onChange={(e) => set({ companyPhone: e.target.value })} />
            <p className="mt-0.5 text-[11px] text-steel">Shown on the customer pages, and in the reply to anyone who texts back.</p>
          </div>
          <div>
            <Label>Timezone for times in texts</Label>
            <Select value={form.timezone} onChange={(e) => set({ timezone: e.target.value })}>
              {[...new Set([form.timezone, ...TIMEZONES])].filter(Boolean).map((tz) => (
                <option key={tz} value={tz}>
                  {tz.replace("America/", "").replace("_", " ")}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Automatic texts only between</Label>
            <div className="flex items-center gap-2">
              <Input type="time" value={form.sendWindow.start} onChange={(e) => set({ sendWindow: { ...form.sendWindow, start: e.target.value } })} />
              <span className="text-xs text-steel">and</span>
              <Input type="time" value={form.sendWindow.end} onChange={(e) => set({ sendWindow: { ...form.sendWindow, end: e.target.value } })} />
            </div>
            <p className="mt-0.5 text-[11px] text-steel">Later ones wait for the morning. Taps from a job always go right away.</p>
          </div>
        </div>
      </Card>

      <Card className="space-y-3">
        <CardTitle>Automatic texts</CardTitle>
        {!autosOn ? <PlanLocked feature="autoTexts" /> : null}
        <fieldset disabled={!autosOn} className={autosOn ? "space-y-3" : "space-y-3 opacity-50"}>
        <label className="flex items-start gap-2 text-sm text-ink">
          <input type="checkbox" className="mt-1" checked={form.autoIntro === "on_assign"} onChange={(e) => set({ autoIntro: e.target.checked ? "on_assign" : "off" })} />
          <span>
            <strong>Who's coming</strong>: when a tech is assigned to a visit in the next day and a half, text the customer their name and
            a link to their photo and bio. (Techs fill in <Link href="/profile" className="text-brandorange-dark underline">My profile</Link>; you approve it on Team.)
          </span>
        </label>
        <label className="flex items-start gap-2 text-sm text-ink">
          <input type="checkbox" className="mt-1" checked={form.reviewRequest.enabled} onChange={(e) => setReview({ enabled: e.target.checked })} />
          <span>
            <strong>How did we do?</strong>: after a job is finished, text a link to rate the visit. Low ratings alert you right away; everyone
            also sees your Google review button.
          </span>
        </label>
        {form.reviewRequest.enabled ? (
          <div className="ml-6 space-y-2 rounded-lg bg-cream/60 p-2 text-xs text-ink">
            <p className="rounded bg-brandorange/10 p-2 text-brandorange-dark">
              Before turning this on for real customers, the Twilio texting campaign must list review requests (Clevis support
              updates it). Until then, keep it off.
            </p>
            <label className="flex items-center gap-2">
              Send
              <Input type="number" min="0" max="1440" className="w-20 py-1 text-xs" value={form.reviewRequest.delayMinutes} onChange={(e) => setReview({ delayMinutes: Math.max(0, parseInt(e.target.value || "0", 10)) })} />
              minutes after the job is finished
            </label>
            <div>
              For these job types (none ticked = all):
              <div className="mt-1 flex flex-wrap gap-2">
                {types.map((t) => (
                  <label key={t} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={form.reviewRequest.jobTypes.includes(t)}
                      onChange={(e) => setReview({ jobTypes: e.target.checked ? [...form.reviewRequest.jobTypes, t] : form.reviewRequest.jobTypes.filter((x) => x !== t) })}
                    />
                    {t}
                  </label>
                ))}
              </div>
            </div>
          </div>
        ) : null}
        <label className="flex items-start gap-2 text-sm text-ink">
          <input type="checkbox" className="mt-1" checked={form.reminder.enabled} onChange={(e) => setReminder({ enabled: e.target.checked })} />
          <span>
            <strong>Night-before reminder</strong>: the evening before a scheduled visit, text the customer the day and time. Each job
            type can have its own words, or none.
          </span>
        </label>
        {form.reminder.enabled ? (
          <div className="ml-6 space-y-2 rounded-lg bg-cream/60 p-2 text-xs text-ink">
            <label className="flex flex-wrap items-center gap-2">
              Send at
              <Input type="time" className="w-28 py-1 text-xs" value={form.reminder.sendAt} onChange={(e) => setReminder({ sendAt: e.target.value })} />
              the day before, your time (keep it inside the window above).
            </label>
            <div>
              <Label>The words (every job type unless it has its own)</Label>
              <textarea className={TEXTAREA} rows={3} value={form.reminder.body} placeholder={DEFAULT_REMINDER_BODY} onChange={(e) => setReminder({ body: e.target.value })} />
              <p className="mt-0.5 text-[11px] text-steel">Fills in: {REMINDER_FIELDS.map((f) => `{{${f}}}`).join(" ")}</p>
              <p className="mt-0.5 text-[11px] text-ink">
                Preview: <em>{preview("")}</em>
              </p>
            </div>
            <div className="space-y-1">
              {types.map((t) => {
                const own = form.reminder.jobTypes[t] ?? { enabled: true, body: "" };
                return (
                  <div key={t} className="rounded-lg border border-steel/15 bg-white/60 p-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={own.enabled} onChange={(e) => setReminderType(t, { ...own, enabled: e.target.checked })} />
                      <strong>{t}</strong>
                      {!own.enabled ? <span className="text-steel">no reminder</span> : null}
                    </label>
                    {own.enabled ? (
                      <>
                        <textarea className={cn(TEXTAREA, "mt-1")} rows={2} value={own.body} placeholder="Same words as above" onChange={(e) => setReminderType(t, { ...own, body: e.target.value })} />
                        {own.body.trim() ? (
                          <p className="mt-0.5 text-[11px] text-steel">
                            Preview: <em>{preview(own.body)}</em>
                          </p>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-steel">
              Goes to the job's customer. On builder work that is the builder, so leave those types off until texts can go to
              the homeowner.
            </p>
          </div>
        ) : null}
        <div className="rounded-lg bg-cream/60 p-2 text-xs text-ink">
          <div className="font-semibold">On builder lot jobs, texts go to</div>
          <label className="mr-4 inline-flex items-center gap-1">
            <input type="radio" name="lotTextsTo" checked={form.lotTextsTo === "customer"} onChange={() => set({ lotTextsTo: "customer" })} />
            the job's customer (the builder)
          </label>
          <label className="inline-flex items-center gap-1">
            <input type="radio" name="lotTextsTo" checked={form.lotTextsTo === "homeowner"} onChange={() => set({ lotTextsTo: "homeowner" })} />
            the homeowner on the lot, when the lot has a phone (else the builder)
          </label>
          <p className="mt-0.5 text-[11px] text-steel">Covers who's coming, on the way, reminders and review asks. A homeowner's STOP is remembered on the lot.</p>
        </div>
        </fieldset>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Google review link</Label>
            <Input value={form.googleReviewUrl} placeholder="https://g.page/r/…/review" onChange={(e) => set({ googleReviewUrl: e.target.value })} />
          </div>
          <div>
            <Label>Facebook review link (optional)</Label>
            <Input value={form.facebookUrl} placeholder="https://facebook.com/…/reviews" onChange={(e) => set({ facebookUrl: e.target.value })} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button busy={busy} onClick={() => void save()}>
            Save texting settings
          </Button>
          {note ? <span className="text-xs text-steel">{note}</span> : null}
        </div>
      </Card>

      <Card className="p-0">
        <CardTitle className="px-4 pt-3">What customers said ({feedback.length})</CardTitle>
        {feedback.map((f) => (
          <div key={f.id} className={cn("flex items-start justify-between gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0", f.stars <= 3 && !f.resolvedAt && "bg-danger/5")}>
            <Link href={`/jobs/${f.jobId}`} className="min-w-0">
              <span className="font-semibold text-ink">{"★".repeat(f.stars)}{"☆".repeat(5 - f.stars)}</span>
              {f.comment ? <span className="ml-2 text-ink">{f.comment}</span> : null}
              <span className="block text-[11px] text-steel">
                {new Date(f.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                {f.googleClicked ? " · opened Google" : ""}
                {f.resolvedAt ? " · handled" : ""}
              </span>
            </Link>
            {f.stars <= 3 && !f.resolvedAt && tenantId ? (
              <Button variant="ghost" className="shrink-0 px-2 py-0.5 text-xs" onClick={() => void call("resolveCustomerFeedback", { tenantId, feedbackId: f.id })}>
                Mark handled
              </Button>
            ) : null}
          </div>
        ))}
        {feedback.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">No ratings yet.</p> : null}
      </Card>

      <Card className="p-0">
        <CardTitle className="px-4 pt-3">Recent texts</CardTitle>
        {messages.map((m) => (
          <div key={m.id} className="border-b border-steel/10 px-4 py-1.5 text-xs last:border-0">
            <div className="flex justify-between gap-2">
              <span className={cn("font-semibold", m.direction === "in" ? "text-brandorange-dark" : m.status === "sent" ? "text-ink" : "text-danger")}>
                {m.direction === "in" ? "Customer replied" : m.status === "sent" ? `Sent: ${m.kind.replace(/_/g, " ")}` : `Not sent: ${m.kind.replace(/_/g, " ")}`}
              </span>
              <span className="shrink-0 text-steel">
                {new Date(m.createdAt).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                {m.actorName ? ` · ${m.actorName}` : ""}
              </span>
            </div>
            <div className="text-steel">{m.status === "sent" || m.direction === "in" ? m.body : m.reason}</div>
            {m.jobId ? (
              <Link href={`/jobs/${m.jobId}`} className="text-brandorange-dark hover:underline">
                Open the job
              </Link>
            ) : null}
          </div>
        ))}
        {messages.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">Nothing sent yet.</p> : null}
      </Card>
    </div>
  );
}
